/**
 * Permission Gate Component
 * 
 * This component conditionally renders content based on user permissions.
 * It can be used to show/hide specific UI elements based on what the user
 * is allowed to access.
 */

import React from 'react';
import { useAuth } from './AuthProvider';

interface PermissionGateProps {
  children: React.ReactNode;
  requiredPermissions?: string[];
  fallback?: React.ReactNode;
  requireAll?: boolean;
}

const PermissionGate: React.FC<PermissionGateProps> = ({ 
  children, 
  requiredPermissions = [],
  fallback = null,
  requireAll = true 
}) => {
  const { user, permissions, isLoading } = useAuth();

  // Show loading state while checking permissions
  if (isLoading) {
    return null;
  }

  // If no permissions required, always show content
  if (requiredPermissions.length === 0) {
    return <>{children}</>;
  }

  // Check if user has required permissions
  const hasRequiredPermissions = requiredPermissions.every(permission => {
    if (!permissions || typeof permission !== 'string') return false;
    
    // Special case for admin role - check user.role instead of permissions
    if (permission === 'admin') {
      return user?.role === 'admin';
    }
    
    // Check all permission arrays for other permissions
    const allPermissions = [
      ...permissions.valueChains,
      ...permissions.countries,
      ...permissions.dataPermissions
    ];
    
    return allPermissions.some(perm => 
      perm.permission_level === permission ||
      (permission.includes('_') && perm.permission_level === permission.split('_')[0])
    );
  });

  // Check if user has any of the required permissions (if requireAll is false)
  const hasAnyRequiredPermissions = requiredPermissions.some(permission => {
    if (!permissions || typeof permission !== 'string') return false;
    
    // Special case for admin role - check user.role instead of permissions
    if (permission === 'admin') {
      return user?.role === 'admin';
    }
    
    // Check all permission arrays for other permissions
    const allPermissions = [
      ...permissions.valueChains,
      ...permissions.countries,
      ...permissions.dataPermissions
    ];
    
    return allPermissions.some(perm => 
      perm.permission_level === permission ||
      (permission.includes('_') && perm.permission_level === permission.split('_')[0])
    );
  });

  // Determine if content should be shown
  const shouldShowContent = requireAll ? hasRequiredPermissions : hasAnyRequiredPermissions;

  // Return content or fallback
  if (shouldShowContent) {
    return <>{children}</>;
  }

  return fallback ? <>{fallback}</> : null;
};

export default PermissionGate;
